﻿import { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/** Props do ErrorBoundary */
export interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

/** Estado interno do ErrorBoundary */
export interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary — Captura erros de renderização e exibe fallback amigável.
 * Envolva módulos inteiros para evitar crash da aplicação toda.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
            <AlertTriangle size={32} className="text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">Algo deu errado</h2>
          <p className="text-muted-foreground text-sm max-w-md mb-6">
            Ocorreu um erro inesperado ao carregar este módulo. Tente novamente ou atualize a página.
          </p>
          {this.state.error && (
            <pre className="text-xs text-red-400 bg-red-50 rounded-lg p-3 mb-4 max-w-md overflow-x-auto">
              {this.state.error.message}
            </pre>
          )}
          <button
            onClick={this.handleRetry}
            className="btn-primary"
          >
            <RefreshCw size={16} />
            Tentar novamente
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
